import { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import Title from '../../components/Title';
import {
    allClients,
    clientsLoadingStatus,
    getClients,
    deleteClient,
    clientsMeta,
    getClientsMaintenanceProgress,
    clientsMaintenanceProgressLoadingStatus,
    clientsMaintenanceProgress,
    createClientDataStore,
    getClientHealthCheck,
    getClientReleaseVersion,
} from '../../redux/slices/clientsSlice';
import CustomTable from '../../components/CustomTable';
import CustomModal from '../../components/CustomModal';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Pagination from '../../components/Pagination';
import { Dropdown, Space, Switch } from 'antd';
import { MoreOutlined } from '@ant-design/icons';

export default function ClientsPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const clients = useSelector(allClients);
    const clientsStatus = useSelector(clientsLoadingStatus);
    const clientsMetaInformation = useSelector(clientsMeta);
    const clientsMaintenanceProgressStatus = useSelector(
        clientsMaintenanceProgressLoadingStatus,
    );
    const clientsMaintenance = useSelector(clientsMaintenanceProgress);
    const [isDeleteClientModalVisible, setIsDeleteClientModalVisible] =
        useState(false);
    const [isHealthChekModalVisible, setIsHealthChekModalVisible] =
        useState(false);
    const [isReleaseVersionModalVisible, setIsReleaseVersionModalVisible] =
        useState(false);
    const [clientName, setClientName] = useState('');
    const [clientId, setClientId] = useState('');
    const [clientAppData, setClientAppData] = useState({
        status: '',
        hasError: false,
        releaseVersion: '',
    });
    const [filter, setFilter] = useState('');
    const joinedClientsWithMaintenanceProgress = clients?.map((client) => {
        return {
            ...client,
            is_maintenance_in_progress: clientsMaintenance?.find(
                ({ client_id }) => client_id === client.client_uid,
            )?.is_maintenance_in_progress,
        };
    });

    const fetchData = useCallback(
        async (page) => {
            try {
                await Promise.all([
                    dispatch(
                        getClients({
                            name: '',
                            page,
                        }),
                    ),
                    dispatch(getClientsMaintenanceProgress()),
                ]);
            } catch (error) {
                toast.error(error.message);
            }
        },
        [dispatch],
    );

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const toggleDeleteClientModal = useCallback(
        (e) => {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }
            setIsDeleteClientModalVisible(!isDeleteClientModalVisible);
        },
        [isDeleteClientModalVisible],
    );

    const handleToggleMaintenanceOn = async (checked, record, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        const { client_uid } = record;

        updateMaintenanceFieldOnDatastore(client_uid, checked);
        fetchData(clientsMetaInformation?.current_page);
    };

    const updateMaintenanceFieldOnDatastore = async (
        clientId,
        is_maintenance_in_progress,
    ) => {
        const data = {
            fields: [
                {
                    key_name: 'is_maintenance_in_progress',
                    value: is_maintenance_in_progress,
                },
            ],
        };
        try {
            await dispatch(
                createClientDataStore({
                    id: clientId,
                    body: data,
                    collectionName: 'clients',
                }),
            ).unwrap();
        } catch (error) {
            toast.error(error.errors);
        }
    };

    const onAddClientClick = () => {
        navigate('create');
    };

    const onDeleteClient = async () => {
        try {
            await dispatch(deleteClient(clientId)).unwrap();
            toast.success('Client deleted successfully');
            toggleDeleteClientModal();
            dispatch(getClients({ name: '' }));
        } catch (error) {
            toast.error(error.errors);
        }
    };

    const getFilteredClients = (data, filter) => {
        return data
            ?.map((el) => {
                const newEl = { ...el };

                if (newEl.created_at) {
                    newEl.created_at = moment(el.created_at).format(
                        'YYYY-MM-DD HH:mm',
                    );
                }

                return newEl;
            })
            .filter((el) =>
                el?.name.toLowerCase().includes(filter.toLowerCase()),
            );
    };

    const onAsyncFilterChange = (value = '') => {
        const data = { name: value };
        setFilter(value);
        dispatch(getClients(data));
    };

    const getClientsPage = (page) => {
        dispatch(
            getClients({
                name: filter,
                page,
            }),
        );
    };

    const paginationControlls = {
        previousClick: getClientsPage,
        nextClick: getClientsPage,
        pageChange: getClientsPage,
    };

    const resetClientAppData = () => {
        setClientAppData({ status: '', hasError: false, releaseVersion: '' });
    };

    const closeHealthCheckModal = () => {
        setIsHealthChekModalVisible(false);
        resetClientAppData();
    };

    const closeReleaseVersionModal = () => {
        setIsReleaseVersionModalVisible(false);
        resetClientAppData();
    };

    const getHealthCheck = useCallback(
        async (apiUrl) => {
            try {
                const status = await dispatch(
                    getClientHealthCheck(apiUrl),
                ).unwrap();
                setClientAppData((prev) => ({ ...prev, status }));
                setIsHealthChekModalVisible(true);
            } catch (error) {
                setClientAppData((prev) => ({
                    ...prev,
                    hasError: true,
                }));
                setIsHealthChekModalVisible(true);
            }
        },
        [dispatch],
    );

    const getReleaseVersion = useCallback(
        async (apiUrl) => {
            try {
                const data = await dispatch(
                    getClientReleaseVersion(apiUrl),
                ).unwrap();
                setClientAppData((prev) => ({
                    ...prev,
                    releaseVersion: data?.version,
                }));
                setIsReleaseVersionModalVisible(true);
            } catch (error) {
                setClientAppData((prev) => ({
                    ...prev,
                    hasError: true,
                }));
                setIsReleaseVersionModalVisible(true);
            }
        },
        [dispatch],
    );

    const handleDropdownItem = useCallback(
        (e, record) => {
            const { domEvent, key } = e;
            const { api_url } = record;
            domEvent?.stopPropagation();

            setClientName(record.name);
            setClientId(record.id);

            if (key === '1') {
                getReleaseVersion(api_url);
            }

            if (key === '2') {
                getHealthCheck(api_url);
            }

            if (key === '3') {
                toggleDeleteClientModal(domEvent);
            }
        },
        [getHealthCheck, getReleaseVersion, toggleDeleteClientModal],
    );

    const actionsOption = {
        title: 'Actions',
        render: (_, record) => (
            <Dropdown
                menu={{
                    items: [
                        {
                            key: '1',
                            label: <span>Release version</span>,
                        },
                        {
                            key: '2',
                            label: <span>Health check</span>,
                        },
                        {
                            key: '3',
                            label: <span>Delete</span>,
                        },
                    ],
                    onClick: (e) => handleDropdownItem(e, record),
                }}
                placement="bottomRight"
                trigger={['click']}
                overlayClassName="ant-dropdown-custom"
            >
                <button
                    className="button__dropdown-dots"
                    onClick={(e) => e.stopPropagation()}
                >
                    <MoreOutlined />
                </button>
            </Dropdown>
        ),
    };

    const maintenanceOption = {
        title: 'Maintenance on',
        render: (_, record) => (
            <Space size="small">
                <Switch
                    onChange={(checked, e) =>
                        handleToggleMaintenanceOn(checked, record, e)
                    }
                    checked={record?.is_maintenance_in_progress}
                    className="details-table__switch--general"
                    disabled={!clientsMaintenance.length}
                />
            </Space>
        ),
    };

    return (
        <div>
            <Helmet>
                <title>Console Defentry - Clients</title>
            </Helmet>
            <Title>Clients</Title>
            <div className="table-wrapper">
                <CustomTable
                    tablePannelTitle="Table of clients"
                    tableHeader={[
                        'ID',
                        'Client name',
                        'URL',
                        'ClientID',
                        'Added at',
                        'Maintenance',
                        'Actions',
                    ]}
                    functionalCell={{
                        Actions: actionsOption,
                        Maintenance: maintenanceOption,
                    }}
                    columnsDataMap={{
                        'Added at': 'created_at',
                        'Client name': 'name',
                        URL: 'api_url',
                        ClientID: 'client_uid',
                        Maintenance: 'is_maintenance_in_progress',
                    }}
                    searchInputPlaceholder="Search by name"
                    topPannelButtonText="Add client"
                    isLoading={
                        clientsStatus === 'loading' ||
                        clientsMaintenanceProgressStatus === 'loading'
                    }
                    data={joinedClientsWithMaintenanceProgress}
                    onAddButtonClick={onAddClientClick}
                    getFilteredData={getFilteredClients}
                    asyncFilter={onAsyncFilterChange}
                    withNavigation="dashboard"
                    withFilter
                />
                {clientsStatus === 'succeeded' &&
                    clientsMaintenanceProgressStatus !== 'loading' && (
                        <Pagination
                            data={clientsMetaInformation}
                            controlls={paginationControlls}
                        />
                    )}
            </div>
            <CustomModal
                title={
                    <p>
                        Are you sure you want to delete
                        <span className="highlighted-text">{` ${clientName} `}</span>
                        client?
                    </p>
                }
                subText="You will delete this client without recovery"
                subTextAlign="center"
                isModalVisible={isDeleteClientModalVisible}
                onAcceptButtonClick={onDeleteClient}
                onCancelButtonClick={toggleDeleteClientModal}
                cancelButtonText="Cancel"
                acceptButtonText="Delete"
            ></CustomModal>
            <CustomModal
                title={
                    <>
                        <p>
                            Client:
                            <span className="highlighted-text">{` ${clientName}`}</span>
                        </p>

                        {clientAppData?.hasError ? (
                            <p>
                                Issue:
                                <span className="highlighted-text">{` Client is not responding`}</span>
                            </p>
                        ) : (
                            <p>
                                Status code:
                                <span className="highlighted-text">{` ${clientAppData?.status}`}</span>
                            </p>
                        )}
                    </>
                }
                isModalVisible={isHealthChekModalVisible}
                onCancelButtonClick={closeHealthCheckModal}
                cancelButtonText="Close"
            />
            <CustomModal
                title={
                    <>
                        <p>
                            Client:
                            <span className="highlighted-text">{` ${clientName}`}</span>
                        </p>
                        {clientAppData?.hasError ? (
                            <p>
                                Issue:
                                <span className="highlighted-text">{` Client is not responding`}</span>
                            </p>
                        ) : (
                            <p>
                                Current release version:
                                <span className="highlighted-text">{` ${clientAppData?.releaseVersion}`}</span>
                            </p>
                        )}
                    </>
                }
                isModalVisible={isReleaseVersionModalVisible}
                onCancelButtonClick={closeReleaseVersionModal}
                cancelButtonText="Close"
            />
        </div>
    );
}
